import React, { useState, useEffect } from 'react';
import CameraCapture from '../components/CameraCapture';
import { CheckCircle, AlertCircle, Loader } from 'lucide-react';

// Public, no-login "Request for Information" form. An applicant opens this via a
// tokenised SMS link (/provide-info?token=…) on their phone. Paul ticks which
// SECTIONS to ask for; only those render here. Answers + document uploads are
// staged on the applicant record for review (and alert Paul on submit).

const API = '/.netlify/functions/applicants-sb';

// CameraCapture returns a data URL (data:image/jpeg;base64,…). Split into the
// { label, photoBase64, contentType } shape the backend expects.
function dataUrlToDoc(label, dataUrl) {
  if (!dataUrl) return null;
  const comma = dataUrl.indexOf(',');
  if (comma < 0) return null;
  const contentType = (dataUrl.slice(0, comma).match(/:(.*?);/) || [])[1] || 'image/jpeg';
  return { label, photoBase64: dataUrl.slice(comma + 1), contentType };
}

const wrap = { maxWidth: 560, margin: '0 auto', padding: '20px 16px 64px' };
const card = { background: 'var(--hp-surface, #fff)', border: '1px solid var(--hp-border, #e5e7eb)', borderRadius: 12, padding: 16, marginBottom: 14 };
const label = { display: 'block', fontSize: '.8rem', fontWeight: 600, color: 'var(--hp-text, #111827)', marginBottom: 5 };
const input = { width: '100%', padding: '11px 12px', borderRadius: 8, border: '1px solid var(--hp-border, #d1d5db)', fontSize: '16px', boxSizing: 'border-box', background: 'var(--hp-bg, #fff)', color: 'var(--hp-text, #111)' };
const sectionTitle = { fontSize: '.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--hp-gold, #b8923a)', marginBottom: 12 };
const fieldGap = { marginBottom: 12 };

function Field({ lbl, children, hint }) {
  return (
    <div style={fieldGap}>
      <label style={label}>{lbl}</label>
      {children}
      {hint && <div style={{ fontSize: '.7rem', color: 'var(--hp-dim, #9ca3af)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}
function Radio({ value, current, onChange, children }) {
  const active = current === value;
  return (
    <button type="button" onClick={() => onChange(value)}
      style={{ flex: 1, padding: '11px 8px', borderRadius: 8, fontSize: '.85rem', fontWeight: 600, cursor: 'pointer',
        border: `1.5px solid ${active ? 'var(--hp-gold, #b8923a)' : 'var(--hp-border, #d1d5db)'}`,
        background: active ? 'var(--hp-gold, #b8923a)' : 'transparent', color: active ? '#fff' : 'var(--hp-text, #374151)' }}>
      {children}
    </button>
  );
}

export default function InfoRequestPage() {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [prefill, setPrefill] = useState(null);
  const [sections, setSections] = useState([]);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false); // false | true | 'already'

  const [f, setF] = useState({
    dateOfBirth: '',
    email: '', mobile: '', currentAddress: '',
    emergencyName: '', emergencyPhone: '', emergencyRelationship: '',
    citizen: '', country: '', visaType: '', grantNumber: '', passportNumber: '', vevoConsent: false,
    tfn: '', superFund: '', superMember: '',
    accountName: '', bsb: '', accNumber: '',
  });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const [idFront, setIdFront] = useState(null);
  const [idBack, setIdBack] = useState(null);
  const [visaDoc, setVisaDoc] = useState(null);

  const has = (k) => sections.includes(k);

  useEffect(() => {
    if (!token) { setError('This link is missing its token. Please use the exact link you were sent.'); setLoading(false); return; }
    fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'getInfoRequest', token }) })
      .then(r => r.json())
      .then(d => {
        if (!d || !d.success) { setError((d && d.error) || 'This link is not valid.'); return; }
        setPrefill(d);
        setSections(Array.isArray(d.sections) ? d.sections : []);
        setNote(d.note || '');
        setF(p => ({ ...p, email: d.email || '', mobile: d.mobile || '', dateOfBirth: d.dateOfBirth || '', ...(d.data || {}) }));
        if (d.submittedAt) setDone('already');
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  async function submit() {
    setError('');
    if (has('dob') && !f.dateOfBirth) return setError('Please enter your date of birth.');
    if (has('photo_id') && !idFront) return setError('Please add a photo of your ID.');
    if (has('work_rights')) {
      if (!f.citizen) return setError('Please tell us whether you’re an Australian citizen / resident.');
      if (f.citizen === 'no' && (!f.country || !f.visaType || !f.grantNumber)) return setError('Please complete your country, visa type and grant number.');
      if (f.citizen === 'no' && !f.passportNumber) return setError('Please enter your passport number.');
      if (f.citizen === 'no' && !f.dateOfBirth) return setError('Please enter your date of birth.');
      if (f.citizen === 'no' && !f.vevoConsent) return setError('Please tick the box to agree to the work-rights check.');
      if (!visaDoc) return setError('Please upload your passport / visa document.');
    }
    if (has('tfn_super_bank')) {
      if (!f.tfn) return setError('Please enter your Tax File Number.');
      if (!f.accountName || !f.bsb || !f.accNumber) return setError('Please enter your bank account name, BSB and account number.');
    }
    if (has('contact_emergency')) {
      if (!f.currentAddress) return setError('Please enter your current residential address.');
      if (!f.emergencyName || !f.emergencyPhone) return setError('Please add an emergency contact name and phone.');
    }

    setSubmitting(true);
    const documents = [];
    if (has('photo_id')) {
      const a = dataUrlToDoc('Photo ID — Front', idFront); if (a) documents.push(a);
      const b = dataUrlToDoc('Photo ID — Back', idBack);   if (b) documents.push(b);
    }
    if (has('work_rights')) {
      const v = dataUrlToDoc(f.citizen === 'no' ? 'Passport / Visa' : 'Citizenship / Passport', visaDoc);
      if (v) documents.push(v);
    }
    try {
      const res = await fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'submitInfoRequest', token, answers: f, documents }) });
      const d = await res.json();
      if (d && d.success) setDone(true);
      else setError((d && d.error) || 'Could not submit — please check your connection and try again.');
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  }

  if (loading) return <div style={{ ...wrap, textAlign: 'center', paddingTop: 80, color: 'var(--hp-dim,#9ca3af)' }}><Loader size={28} style={{ animation: 'spin 1s linear infinite' }} /><div style={{ marginTop: 10 }}>Loading…</div></div>;

  if (error && !prefill) return (
    <div style={{ ...wrap, paddingTop: 60 }}>
      <div style={{ ...card, textAlign: 'center' }}>
        <AlertCircle size={32} color="#dc2626" />
        <div style={{ fontWeight: 700, marginTop: 10 }}>Link not valid</div>
        <div style={{ color: 'var(--hp-dim,#6b7280)', fontSize: '.85rem', marginTop: 6 }}>{error}</div>
      </div>
    </div>
  );

  if (done) return (
    <div style={{ ...wrap, paddingTop: 60 }}>
      <div style={{ ...card, textAlign: 'center' }}>
        <CheckCircle size={40} color="#16a34a" />
        <div style={{ fontWeight: 700, fontSize: '1.1rem', marginTop: 12 }}>{done === 'already' ? 'Already sent' : 'Thank you!'}</div>
        <div style={{ color: 'var(--hp-dim,#6b7280)', fontSize: '.9rem', marginTop: 8 }}>
          {done === 'already'
            ? 'Your details have already been received. If you need to change something, let Paul know.'
            : 'Your details have been sent to Hollypaul. Thanks!'}
        </div>
      </div>
    </div>
  );

  return (
    <div style={wrap}>
      <div style={{ textAlign: 'center', marginBottom: 18 }}>
        <div style={{ fontFamily: 'var(--font-display, serif)', fontWeight: 800, fontSize: '1.4rem', letterSpacing: '.04em', color: 'var(--hp-gold, #b8923a)' }}>HOLLYPAUL</div>
        <div style={{ fontSize: '.95rem', fontWeight: 700, marginTop: 8 }}>Hi{prefill?.fullName ? `, ${prefill.fullName.split(' ')[0]}` : ''}! 👋</div>
        <div style={{ fontSize: '.82rem', color: 'var(--hp-dim,#6b7280)', marginTop: 4 }}>Please complete the details below and send them back to Paul at Hollypaul. It only takes a few minutes.</div>
      </div>

      {note && (
        <div style={{ ...card, background: 'var(--hp-bg, #fffbeb)', border: '1px solid var(--hp-gold, #f0d58a)' }}>
          <div style={{ fontSize: '.85rem', color: 'var(--hp-text, #92400e)', whiteSpace: 'pre-wrap' }}>{note}</div>
        </div>
      )}

      {/* Date of birth */}
      {has('dob') && (
        <div style={card}>
          <div style={sectionTitle}>Date of birth</div>
          <Field lbl="Date of birth"><input style={input} type="date" value={f.dateOfBirth} onChange={e => set('dateOfBirth', e.target.value)} /></Field>
        </div>
      )}

      {/* Photo ID */}
      {has('photo_id') && (
        <div style={card}>
          <div style={sectionTitle}>Photo ID</div>
          <Field lbl="Front of your ID" hint="Driver’s licence, passport or other photo ID. Tap to take a photo or upload a file.">
            <CameraCapture nativeFirst guide="document" maxEdge={1600} quality={0.85} value={idFront} onCapture={setIdFront} onClear={() => setIdFront(null)} />
          </Field>
          <Field lbl="Back of your ID" hint="If your ID has details on the back (e.g. a driver’s licence), add it here. Otherwise leave blank.">
            <CameraCapture nativeFirst guide="document" maxEdge={1600} quality={0.85} value={idBack} onCapture={setIdBack} onClear={() => setIdBack(null)} />
          </Field>
        </div>
      )}

      {/* Work rights / visa */}
      {has('work_rights') && (
        <div style={card}>
          <div style={sectionTitle}>Work rights</div>
          <Field lbl="Are you an Australian citizen or permanent resident?">
            <div style={{ display: 'flex', gap: 8 }}>
              <Radio value="yes" current={f.citizen} onChange={v => set('citizen', v)}>Yes</Radio>
              <Radio value="no" current={f.citizen} onChange={v => set('citizen', v)}>No</Radio>
            </div>
          </Field>
          {f.citizen === 'no' && (
            <>
              <Field lbl="Country of citizenship"><input style={input} value={f.country} onChange={e => set('country', e.target.value)} /></Field>
              <Field lbl="Visa type"><input style={input} value={f.visaType} onChange={e => set('visaType', e.target.value)} placeholder="e.g. Student 500, Working Holiday 417" /></Field>
              <Field lbl="Visa grant number"><input style={input} value={f.grantNumber} onChange={e => set('grantNumber', e.target.value)} /></Field>
              <Field lbl="Passport number" hint="Exactly as printed on your passport."><input style={input} value={f.passportNumber} onChange={e => set('passportNumber', e.target.value.toUpperCase())} autoCapitalize="characters" /></Field>
              {!has('dob') && (
                <Field lbl="Date of birth"><input type="date" style={input} value={f.dateOfBirth} onChange={e => set('dateOfBirth', e.target.value)} /></Field>
              )}
            </>
          )}
          <Field lbl={f.citizen === 'no' ? 'Upload your passport' : 'Upload your passport, citizenship certificate or birth certificate'} hint="Tap to take a photo or upload a file.">
            <CameraCapture nativeFirst guide="document" maxEdge={1600} quality={0.85} value={visaDoc} onCapture={setVisaDoc} onClear={() => setVisaDoc(null)} />
          </Field>
          {f.citizen === 'no' && (
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: '.84rem', lineHeight: 1.45, marginTop: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!f.vevoConsent} onChange={e => set('vevoConsent', e.target.checked)} style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0 }} />
              <span>{(prefill && prefill.vevoConsentText) || 'I consent to Hollypaul checking my visa and work rights with the Department of Home Affairs (VEVO), using my passport details, through its provider vSure.'}</span>
            </label>
          )}
        </div>
      )}

      {/* TFN, super & bank */}
      {has('tfn_super_bank') && (
        <div style={card}>
          <div style={sectionTitle}>Tax, super &amp; bank</div>
          <Field lbl="Tax File Number (TFN)" hint="9 digits."><input style={input} inputMode="numeric" value={f.tfn} onChange={e => set('tfn', e.target.value)} /></Field>
          <Field lbl="Superannuation fund name"><input style={input} value={f.superFund} onChange={e => set('superFund', e.target.value)} placeholder="e.g. Australian Super" /></Field>
          <Field lbl="Super member number"><input style={input} value={f.superMember} onChange={e => set('superMember', e.target.value)} /></Field>
          <Field lbl="Bank account name"><input style={input} value={f.accountName} onChange={e => set('accountName', e.target.value)} /></Field>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ width: 130 }}><Field lbl="BSB"><input style={input} inputMode="numeric" value={f.bsb} onChange={e => set('bsb', e.target.value)} placeholder="000-000" /></Field></div>
            <div style={{ flex: 1 }}><Field lbl="Account number"><input style={input} inputMode="numeric" value={f.accNumber} onChange={e => set('accNumber', e.target.value)} /></Field></div>
          </div>
        </div>
      )}

      {/* Contact & emergency */}
      {has('contact_emergency') && (
        <div style={card}>
          <div style={sectionTitle}>Contact &amp; emergency</div>
          <Field lbl="Mobile"><input style={input} type="tel" value={f.mobile} onChange={e => set('mobile', e.target.value)} /></Field>
          <Field lbl="Email"><input style={input} type="email" value={f.email} onChange={e => set('email', e.target.value)} /></Field>
          <Field lbl="Current residential address"><input style={input} value={f.currentAddress} onChange={e => set('currentAddress', e.target.value)} placeholder="Unit / street / suburb / state / postcode" /></Field>
          <div style={{ ...sectionTitle, marginTop: 6 }}>Emergency contact</div>
          <Field lbl="Name"><input style={input} value={f.emergencyName} onChange={e => set('emergencyName', e.target.value)} /></Field>
          <Field lbl="Phone"><input style={input} type="tel" value={f.emergencyPhone} onChange={e => set('emergencyPhone', e.target.value)} /></Field>
          <Field lbl="Relationship"><input style={input} value={f.emergencyRelationship} onChange={e => set('emergencyRelationship', e.target.value)} placeholder="e.g. partner, parent, friend" /></Field>
        </div>
      )}

      <div style={{ ...card, background: 'var(--hp-bg, #f9fafb)' }}>
        <div style={sectionTitle}>Your privacy &amp; security</div>
        <div style={{ fontSize: '.78rem', color: 'var(--hp-dim, #6b7280)', lineHeight: 1.5 }}>
          Everything you send is <strong>encrypted in transit and stored encrypted</strong>, in private storage — never on
          a public or shareable link. Only Hollypaul’s <strong>authorised admins can open your documents, and only after
          signing in</strong>. We collect this solely to set you up for work and meet our obligations under the Australian
          Privacy Act, and you can ask us to access or delete it at any time.
        </div>
      </div>

      {error && <div style={{ ...card, background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c', fontSize: '.85rem', display: 'flex', gap: 8, alignItems: 'flex-start' }}><AlertCircle size={18} style={{ flexShrink: 0, marginTop: 1 }} />{error}</div>}

      <button type="button" onClick={submit} disabled={submitting}
        style={{ width: '100%', padding: '15px', borderRadius: 10, border: 'none', background: 'var(--hp-gold, #b8923a)', color: '#fff', fontSize: '1rem', fontWeight: 700, cursor: 'pointer', opacity: submitting ? 0.6 : 1 }}>
        {submitting ? 'Sending…' : 'Send my details'}
      </button>
      <div style={{ textAlign: 'center', fontSize: '.7rem', color: 'var(--hp-dim,#9ca3af)', marginTop: 12 }}>Your information is sent securely to Hollypaul Recruitment Pty Ltd.</div>
    </div>
  );
}
